import './OpportunityDetail.css';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { HiChevronUp, HiChevronDown } from 'react-icons/hi2';
import BrandInsights from '../../components/BrandInsights/BrandInsights';
import AllSites from '../../components/AllSites/AllSites';
import ExportedOpportunitiesTab from '../../components/ExportedOpportunitiesTab/ExportedOpportunitiesTab';
import ExportOpportunitiesDialog, { CrmFormData } from '../../components/AllSites/ExportOpportunitiesDialog';
import { createCrmOpportunity, getCrmOpportunities, CrmOpportunityRecord } from '../../services/crmService';
import Spinner from '../../components/Spinner/Spinner';
import BrandLogo from '../../components/BrandsTable/BrandLogo';
import { fetchBrandData } from '../../services/brandSitesService';
import { fetchBrandsByUltimateParent, aggregateBrandData } from '../../services/brandService';
import { exportOpportunitiesByRegionData } from '../../utils/excelExportUtils';
import toast from 'react-hot-toast';
import { trackCrmEvent } from '../../utils/posthogUtils';
import { useExportStore, selectStoresForBrand } from '../../stores';
import AddToListDialog from '../../components/AddToListDialog/AddToListDialog';
import Tooltip from '../../components/Tooltip/Tooltip';
import OpenInClaudeDialog from '../../components/OpenInClaudeDialog/OpenInClaudeDialog';
import { isTauri } from '../../utils/tauri';
import ImportDialog from '../../components/ImportDialog/ImportDialog';

type TabType = 'brand-insights' | 'all-sites' | 'exported-opportunities';

const toTitleCase = (str: string) =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

function OpportunityDetail() {
  const navigate = useNavigate();
  const { brandname } = useParams<{ brandname?: string }>();
  const [searchParams] = useSearchParams();
  const queryType = searchParams.get('type'); // 'ultimateparent' or null for brand
  const [activeTab, setActiveTab] = useState<TabType>('all-sites');
  const [loading, setLoading] = useState(true);
  const [brandName, setBrandName] = useState('');
  const [brandStats, setBrandStats] = useState({
    name: '',
    totalSites: 0,
    acquiredSites: 0,
    gkapDistribution: {} as any,
    solutions: {
      purchek: 0,
      cartContainment: 0,
      cartManager: 0,
      subscription: 0,
      faceFirst: 0
    }
  });
  const [stores, setStores] = useState<any[]>([]);
  const [processedStores, setProcessedStores] = useState<any[]>([]);
  const exportBrandKey = brandName || (brandname ? decodeURIComponent(brandname).toUpperCase() : '');
  const selectedStorePoiIds = useExportStore(selectStoresForBrand(exportBrandKey));
  const [currentSortBy, setCurrentSortBy] = useState<string>('rank');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [crmStoreIds, setCrmStoreIds] = useState<Set<string>>(new Set());
  const [crmOpportunities, setCrmOpportunities] = useState<CrmOpportunityRecord[]>([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmFetched, setCrmFetched] = useState(false);
  const [saveToListDialogOpen, setSaveToListDialogOpen] = useState(false);
  const [selectedSubBrands, setSelectedSubBrands] = useState<Set<string>>(new Set());
  const [subBrandsExpanded, setSubBrandsExpanded] = useState(true);
  const [openInClaudeOpen, setOpenInClaudeOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Get unique sub-brands with counts, sorted by count descending
  const subBrandsWithCounts = useMemo(() => {
    if (queryType !== 'ultimateparent' || stores.length === 0) return [];
    const countMap: Record<string, number> = {};
    stores.forEach((s: any) => {
      if (s.brand_name) countMap[s.brand_name] = (countMap[s.brand_name] || 0) + 1;
    });
    return Object.entries(countMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [stores, queryType]);

  const toggleSubBrand = (brand: string) => {
    setSelectedSubBrands(prev => {
      const next = new Set(prev);
      if (next.has(brand)) {
        next.delete(brand);
      } else {
        next.add(brand);
      }
      return next;
    });
  };

  // Filter stores by selected sub-brands (empty = all)
  const filteredStoresBySubBrand = useMemo(() => {
    if (selectedSubBrands.size === 0) return stores;
    return stores.filter((s: any) => selectedSubBrands.has(s.brand_name));
  }, [stores, selectedSubBrands]);

  // Fetch CRM data only when Exported Opportunities tab is opened
  useEffect(() => {
    if (activeTab !== 'exported-opportunities' || !brandName || crmFetched) return;
    const loadCrm = async () => {
      setCrmLoading(true);
      const to = new Date();
      const from = new Date('2026-04-15');
      const result = await getCrmOpportunities(
        from.toISOString().split('T')[0],
        to.toISOString().split('T')[0],
        1,
        brandName
      );
      if (result.success) {
        setCrmOpportunities(result.opportunities);
        const ids = new Set<string>();
        for (const opp of result.opportunities) {
          for (const store of opp.OpportunityStores || []) {
            if (store.storeNumber) ids.add(store.storeNumber);
            if (store.atlas_id) ids.add(store.atlas_id);
          }
        }
        setCrmStoreIds(ids);
      }
      setCrmLoading(false);
      setCrmFetched(true);
    };
    loadCrm();
  }, [activeTab, brandName, crmFetched]);

  // Initialize loading state and set brand name
  useEffect(() => {
    const load = async () => {
      try {
        // Convert URL parameter to brand name if available
        if (brandname) {
          const formattedName = decodeURIComponent(brandname).toUpperCase();
          setBrandName(formattedName);

          if (queryType === 'ultimateparent') {
            // Fetch all brands under this ultimate parent
            const brandsList = await fetchBrandsByUltimateParent(formattedName);
            const aggregatedStats = aggregateBrandData(brandsList);

            setBrandStats((prev) => ({
              ...prev,
              name: aggregatedStats.name,
              totalSites: aggregatedStats.total_pois?.aggregate?.count || 0,
              acquiredSites: aggregatedStats.customers?.aggregate?.count || 0,
              gkapDistribution: {}
            }));

            // Fetch stores for all brands in a single query
            const brandNamesToFetch = brandsList.map((b: any) => b.name);
            const { stores: allStores } = await fetchBrandData(brandNamesToFetch);
            setStores(allStores);
          } else {
            // Fetch single brand data (original behavior)
            const { stats, stores: fetchedStores } = await fetchBrandData(formattedName);

            setBrandStats((prev) => ({
              ...prev,
              name: stats.name,
              totalSites: stats.totalSites,
              acquiredSites: stats.acquiredSites,
              gkapDistribution: stats.gkapDistribution
            }));

            // Set stores for CSV export
            setStores(fetchedStores);
          }
        }
      } catch (err) {
        console.error('Failed to load brand data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [brandname, queryType]);

  const [recommendedOpportunities, setRecommendedOpportunities] = useState(0);


  const selectedRecommendedCount = useMemo(() => {
    if (selectedStorePoiIds.size === 0) return 0;
    const source = processedStores.length > 0 ? processedStores : [];
    const selected = source.filter((s: any) => selectedStorePoiIds.has(s.poiId || s.storeId || ''));
    let count = 0;
    selected.forEach((s: any) => {
      if (s.recommendations) {
        if (s.recommendations.purchek?.length > 0) count++;
        if (s.recommendations.containment?.length > 0) count++;
        if (s.recommendations.cartManager?.length > 0) count++;
        if (s.recommendations.subscription?.length > 0) count++;
        if (s.recommendations.pairedWheel?.length > 0) count++;
      }
    });
    return count;
  }, [selectedStorePoiIds, processedStores]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const [exportDialogInitialStep, setExportDialogInitialStep] = useState<1 | 2>(1);

  const handleExport = () => {
    setExportDialogInitialStep(1);
    setExportDialogOpen(true);
  };

  const handleDownloadXlsx = () => {
    handleExportStores('selected');
  };

  const handleExportCrmDialog = () => {
    setExportDialogInitialStep(2);
    setExportDialogOpen(true);
  };

  const handleSaveStoresToList = () => {
    setSaveToListDialogOpen(true);
  };

  const getBulkStoresForList = () => {
    const source = processedStores.length > 0 ? processedStores : stores;
    return source
      .filter((s: any) => selectedStorePoiIds.has(s.poiId || s.storeId || ''))
      .map((s: any) => ({
        poiId: s.poiId || s.storeId || '',
        storeName: (s.storeId || s.updated_store_ids?.[0]?.store_id) ? `${s.brandName || s.brand_name || s.name || s.site || ''} ${s.storeId || s.updated_store_ids?.[0]?.store_id}` : s.name || s.site || '',
        brandName: s.brandName || s.brand_name || brandName,
      }));
  };

  const getSelectedStores = () => {
    const source = processedStores.length > 0 ? processedStores : stores;
    if (selectedStorePoiIds.size > 0) {
      return source.filter((s: any) => selectedStorePoiIds.has(s.poiId || s.storeId || ''));
    }
    return source;
  };

  const handleExportStores = (scope: 'selected' | 'all') => {
    const allStores = processedStores.length > 0 ? processedStores : stores;
    const exportStores = scope === 'selected' ? getSelectedStores() : allStores;

    if (exportStores.length > 0) {
      exportOpportunitiesByRegionData(exportStores, brandName, currentSortBy, crmStoreIds);
    }
  };

  const handleExportCrm = async (scope: 'selected' | 'all', crmData: CrmFormData) => {
    const exportStores = scope === 'selected' ? getSelectedStores() : stores;

    const opportunityStores = exportStores.map((store: any) => {
      const recommendations: { Solution: string; Reasoning: string }[] = [];
      if (store.recommendations) {
        if (store.recommendations.purchek?.length > 0) {
          recommendations.push({
            Solution: 'Purchek',
            Reasoning: store.recommendations.purchek[0],
          });
        }
        if (store.recommendations.containment?.length > 0) {
          recommendations.push({
            Solution: 'Containment',
            Reasoning: store.recommendations.containment[0],
          });
        }
        if (store.recommendations.cartManager?.length > 0) {
          recommendations.push({
            Solution: 'Cart Manager',
            Reasoning: store.recommendations.cartManager[0],
          });
        }
      }
      return {
        gk_name: store.name || store.site || '',
        atlas_id: store.poiId || store.storeId || '',
        addressLine1: store.location || store.address || '',
        addressLine2: '',
        city: store.city || '',
        state: store.state || '',
        country: 'USA',
        postalCode: store.zipCode?.toString() || '',
        RecommendedSolutions: recommendations,
      };
    });

    const result = await createCrmOpportunity({
      Opportunity: {
        Topic: crmData.topic,
        CurrentSituation: crmData.currentSituation,
        PotentialCustomer: brandName,
        CustomerNeed: crmData.customerNeed,
        CustomerPainPoints: crmData.customerPainPoints,
        EstCloseDate: crmData.estCloseDate,
        EstRevenue: crmData.estRevenue,
        Probability: crmData.probability,
        NoOfStores: opportunityStores.length,
        OpportunitySiteType: crmData.opportunitySiteType,
        PricePerStore: crmData.pricePerStore,
        UltimateParent: brandName,
        Assignee: crmData.assignee || undefined,
        ProposedSolutions: crmData.proposedSolutions,
      },
      OpportunityStores: opportunityStores,
    });

    if (result.success) {
      trackCrmEvent.opportunityCreated(brandName, crmData.estRevenue, opportunityStores.length);
      return { recordUrl: result.data?.RecordUrl || '' };
    } else {
      trackCrmEvent.opportunityCreateFailed(brandName, result.message);
      console.error('CRM export error:', result.message);
      toast.error('Something went wrong. Please try again.');
      throw new Error(result.message);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  return (
    <div className="opportunity-detail-page">
      <div className="detail-header">
        <div className="od-header-left">
          <button className="back-btn" onClick={handleBack}>
            <HiOutlineArrowLeft />
            Back
          </button>
          <div className="detail-header-logo">
            <BrandLogo brandName={brandname ? decodeURIComponent(brandname).toUpperCase() : ''} />
          </div>
          <div className="detail-title">
            <h1>{toTitleCase(brandName)}</h1>
            <p className="detail-subtitle">{queryType === 'ultimateparent' ? 'Ultimate Parent' : 'Brand'}</p>
          </div>
        </div>
        <div className="header-stats">
          <div className="header-stats-item">
            <span className="header-stats-label">Total Stores</span>
            <span className="header-stats-value">{brandStats.totalSites}</span>
          </div>
          <div className="header-stats-item">
            <span className="header-stats-label">Acquired Stores</span>
            <span className="header-stats-value">{brandStats.acquiredSites}</span>
          </div>
          <div className="header-stats-item">
            <span className="header-stats-label">Non Acquired Stores</span>
            <span className="header-stats-value">{brandStats.totalSites - brandStats.acquiredSites}</span>
          </div>
          <div className="header-stats-item">
            <span className="header-stats-label">Recommended Opportunities</span>
            <span className="header-stats-value">{recommendedOpportunities}</span>
          </div>
          {isTauri() && (
            <button className="open-in-claude-btn" onClick={() => setOpenInClaudeOpen(true)}>
              ✳ Open in Claude
            </button>
          )}
        </div>
      </div>

      {isTauri() && (
        <OpenInClaudeDialog
          isOpen={openInClaudeOpen}
          onClose={() => setOpenInClaudeOpen(false)}
          brand={brandName || (brandname ? decodeURIComponent(brandname).toUpperCase() : '')}
          isUltimateParent={queryType === 'ultimateparent'}
        />
      )}

      {queryType === 'ultimateparent' && subBrandsWithCounts.length > 1 && (
        <div className={`sub-brand-section ${subBrandsExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="sub-brand-header">
            <h3 className="sub-brand-title">Opportunities by Subbrands of {toTitleCase(brandName)}</h3>
            <button className="sub-brand-toggle-btn" onClick={() => setSubBrandsExpanded(!subBrandsExpanded)}>
              {subBrandsExpanded ? <HiChevronUp /> : <HiChevronDown />}
              {subBrandsExpanded ? 'Collapse' : 'Expand'}
            </button>
          </div>
          <div className="sub-brand-chart">
            {(() => {
              const maxCount = Math.max(stores.length, ...subBrandsWithCounts.map(b => b.count));
              return (
                <>
                  <div
                    className={`sub-brand-bar-item ${selectedSubBrands.size === 0 ? 'active' : ''}`}
                    onClick={() => setSelectedSubBrands(new Set())}
                  >
                    <div className="sub-brand-bar-collapse">
                      <div className="sub-brand-bar-wrapper">
                        <div className="sub-brand-bar" style={{ height: '100%' }} />
                      </div>
                    </div>
                    <span className="sub-brand-bar-label">All Brands</span>
                    <span className="sub-brand-bar-value">{stores.length}</span>
                  </div>
                  {subBrandsWithCounts.map(({ name, count }) => (
                    <div
                      key={name}
                      className={`sub-brand-bar-item ${selectedSubBrands.has(name) ? 'active' : ''}`}
                      onClick={() => toggleSubBrand(name)}
                    >
                      <div className="sub-brand-bar-collapse">
                        <div className="sub-brand-bar-wrapper">
                          <div
                            className="sub-brand-bar"
                            style={{ height: `${Math.max(10, (count / maxCount) * 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="sub-brand-bar-label">{toTitleCase(name)}</span>
                      <span className="sub-brand-bar-value">{count}</span>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      )}

      <div className="detail-tabs">
        <div className="tabs-container">
          <button
            className={`detail-tab ${activeTab === 'all-sites' ? 'active' : ''}`}
            onClick={() => handleTabChange('all-sites')}
          >
            All Sites
          </button>
          <button
            className={`detail-tab ${activeTab === 'brand-insights' ? 'active' : ''}`}
            onClick={() => handleTabChange('brand-insights')}
          >
            Brand Insights
          </button>
          <Tooltip content="Work in Progress" side="top">
            <button
              className={`detail-tab ${activeTab === 'exported-opportunities' ? 'active' : ''}`}
              onClick={() => handleTabChange('exported-opportunities')}
            >
              Exported Opportunities <span className="wip-tag">WIP</span>
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="detail-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner size="small" />
          </div>
        ) : (
          activeTab === 'brand-insights' ? (
            <BrandInsights brandName={brandname} isUltimateParent={queryType === 'ultimateparent'} />
          ) : activeTab === 'exported-opportunities' ? (
            <ExportedOpportunitiesTab ultimateParent={brandName} crmOpportunities={crmOpportunities} crmLoading={crmLoading} />
          ) : (
            <AllSites brandName={brandname} isUltimateParent={queryType === 'ultimateparent'} stores={filteredStoresBySubBrand} crmStoreIds={crmStoreIds} onRecommendedCountChange={setRecommendedOpportunities} onProcessedStoresChange={setProcessedStores} onSortByChange={setCurrentSortBy} onExport={handleExport} onDownloadXlsx={handleDownloadXlsx} onExportCrm={handleExportCrmDialog} onSaveStoresToList={handleSaveStoresToList} onImport={() => setImportDialogOpen(true)} />
          )
        )}
      </div>

      <ExportOpportunitiesDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        filteredStoresCount={processedStores.length > 0 ? processedStores.length : stores.length}
        totalStoresCount={stores.length}
        selectedRowsCount={selectedStorePoiIds.size}
        recommendedCount={selectedRecommendedCount}
        onExport={handleExportStores}
        onExportCrm={handleExportCrm}
        brandName={brandName}
        onNavigate={navigate}
        selectedStores={getSelectedStores()}
        initialStep={exportDialogInitialStep}
      />

      <AddToListDialog
        isOpen={saveToListDialogOpen}
        onClose={() => setSaveToListDialogOpen(false)}
        type="store"
        brandName={brandName}
        bulkStores={getBulkStoresForList()}
      />

      <ImportDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        brandName={brandName || (brandname ? decodeURIComponent(brandname).toUpperCase() : '')}
        brandId={brandName || (brandname ? decodeURIComponent(brandname).toUpperCase() : '')}
        isUltimateParent={queryType === 'ultimateparent'}
      />
    </div>
  );
}

export default OpportunityDetail;
